"use client";

import Image from "next/image";
import { CaretRightIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { truncateKeyword, type SearchEmptyCause } from "@/lib/search/empty-state";
import { EmptyStateLayout } from "./EmptyStateLayout";

/** 빈 화면 그림 — 홈 카테고리 타일과 같은 3D 자산(검색어·필터 = 카테고리 타일, 지역 = 주황 핀). */
const ILLUSTRATION = {
  browse: "/icons/categories/home-view-all-3d-v3-320.webp",
  area: "/icons/categories/home-nearby-3d-v2-320.webp",
} as const;

export interface SearchEmptyActions {
  /** 가격·이용 방식·카테고리 해제(검색어·지역 유지) */
  onResetFilters: () => void;
  /** 검색어만 지운다(지역·필터 유지) */
  onClearKeyword: () => void;
  /** 같은 지역에서 그 카테고리로 검색(검색어·필터는 뗀다) */
  onPickCategory: (code: string) => void;
  /** 지도를 줌 2단계 넓혀 그 범위로 바로 검색 */
  onWidenArea: () => void;
  /** 내 주변 검색 */
  onNearby: () => void;
  /** 내 주변 — 위치를 받는 중 */
  nearbyPending?: boolean;
}

interface SearchEmptyStateProps extends SearchEmptyActions {
  /** 우선순위 순서의 원인(searchEmptyCauses). 비면 버튼 없는 기본 문구 */
  causes: SearchEmptyCause[];
  keyword: string | null;
  /** 추천 카테고리 한 줄 — 3D 그림이 있는 카테고리만(최대 3개). 비면 줄을 숨긴다. */
  popularCategories: { code: string; label: string; image: string }[];
  wide: boolean;
  t: (key: string, params?: Record<string, string | number>) => string;
}

// 겹친 그림 자리 — 미리보기 B안(왼쪽 −8° · 가운데 위 · 오른쪽 +8°)
const STACK_TOP = [4, 0, 5];
const STACK_ROTATE = [-8, 0, 8];

interface EmptyAction {
  key: string;
  label: string;
  onClick: () => void;
  pending?: boolean;
}

/**
 * 일반 검색 결과 0건 화면(09-29 미리보기 확정안).
 *
 * 제목·설명·그림은 첫째 원인이 정한다. 버튼은 원인 순서대로 늘어서고 맨 앞 하나만 진한
 * 주 버튼 — 결과가 생길 가능성이 가장 큰 행동이다. 그 아래 "이런 카테고리는 어때요?" 한 줄
 * (겹친 3D 그림 + 이름)은 원인과 무관하게 붙는다. 아래 "○○ 추천 상품"은 SearchLayoutVariantA 가 그대로 이어 그린다.
 */
export function SearchEmptyState({
  causes,
  keyword,
  popularCategories,
  wide,
  t,
  onResetFilters,
  onClearKeyword,
  onPickCategory,
  onWidenArea,
  onNearby,
  nearbyPending,
}: SearchEmptyStateProps) {
  const top = causes[0];

  const copy =
    top === "filters"
      ? { title: t("emptyState.filterTitle"), body: t("emptyState.filterBody") }
      : top === "keyword"
        ? {
            title: t("emptyState.keywordTitle", { keyword: truncateKeyword(keyword ?? "") }),
            body: t("emptyState.keywordBody"),
          }
        : top === "area"
          ? { title: t("emptyState.areaTitle"), body: t("emptyState.areaBody") }
          : { title: t("results.noResults"), body: t("results.noResultsHint") };

  const actions: EmptyAction[] = causes.flatMap((cause): EmptyAction[] => {
    switch (cause) {
      case "filters":
        return [{ key: "filters", label: t("emptyState.filterAction"), onClick: onResetFilters }];
      case "keyword":
        return [{ key: "keyword", label: t("emptyState.keywordAction"), onClick: onClearKeyword }];
      case "area":
        return [
          { key: "widen", label: t("emptyState.areaAction"), onClick: onWidenArea },
          {
            key: "nearby",
            label: t("emptyState.nearbyAction"),
            onClick: onNearby,
            pending: nearbyPending,
          },
        ];
    }
  });

  return (
    <EmptyStateLayout
      image={top === "area" ? ILLUSTRATION.area : ILLUSTRATION.browse}
      title={copy.title}
      body={copy.body}
      wide={wide}
    >
      {actions.length > 0 ? (
        <div className={cn("mt-4 flex flex-wrap gap-2", wide ? "justify-start" : "justify-center")}>
          {actions.map((action, i) => (
            <button
              key={action.key}
              type="button"
              onClick={action.onClick}
              disabled={action.pending}
              aria-busy={action.pending || undefined}
              className={cn(
                "h-10 rounded-full border px-4 text-sm font-medium transition disabled:opacity-60",
                i === 0
                  ? "border-gray-900 bg-gray-900 text-white hover:bg-gray-800"
                  : "border-gray-300 bg-white text-gray-900 hover:bg-gray-50",
              )}
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}
      {popularCategories.length > 0 ? (
        // 한 줄 = 한 행동: 맨 앞 카테고리로 같은 지역에서 검색한다(09-29 B안).
        <button
          type="button"
          onClick={() => onPickCategory(popularCategories[0].code)}
          className="mt-[18px] flex w-full max-w-[290px] items-center gap-3 rounded-2xl bg-gray-100 px-3 py-2.5 text-left transition hover:bg-gray-200/70"
        >
          <span
            aria-hidden
            className="relative h-11 shrink-0"
            style={{ width: 40 + 28 * (popularCategories.length - 1) }}
          >
            {popularCategories.map((category, i) => (
              <Image
                key={category.code}
                src={category.image}
                alt=""
                width={40}
                height={40}
                unoptimized
                className={cn(
                  "absolute size-10 object-contain drop-shadow-[0_3px_5px_rgba(17,24,39,0.2)]",
                  // 세 장이면 가운데가 위, 양옆은 살짝 기울인다
                  i === 1 && "z-10",
                )}
                style={{
                  left: i * 28,
                  top: STACK_TOP[i] ?? 0,
                  transform: popularCategories.length > 1 ? `rotate(${STACK_ROTATE[i] ?? 0}deg)` : undefined,
                }}
              />
            ))}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-semibold text-gray-900">
              {t("emptyState.suggestTitle")}
            </span>
            <span className="truncate text-xs text-gray-500">
              {popularCategories.map((category) => category.label).join(" · ")}
            </span>
          </span>
          <CaretRightIcon size={16} className="shrink-0 text-gray-400" />
        </button>
      ) : null}
    </EmptyStateLayout>
  );
}
